import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { EditorSystem } from '../../../../systems/editor/EditorSystem';
import { EditorTool } from '../../../../../shared/TileEditorTypes';
import { LevelEntity } from '../../../../../shared/LevelTypes';
import { gameLogger } from '../../../../../utils/GameLogger';
import { EventBus } from '../../../../../shared/EventBus';
import { TeleporterData, TeleporterLink } from './TeleporterTypes';

export function useTeleporterState(editorSystem: EditorSystem | null) {
  const [teleporters, setTeleporters] = useState<TeleporterData[]>([]);
  const [selectedTeleporterId, setSelectedTeleporterId] = useState<string | null>(null);
  const [showLinkLines, setShowLinkLines] = useState(true);
  const [linkColor, setLinkColor] = useState('#9370DB');
  const [isLinkingMode, setIsLinkingMode] = useState(false);

  const [targetId, setTargetId] = useState('');
  const [targetMapId, setTargetMapId] = useState('');
  const [collidesWithProjectiles, setCollidesWithProjectiles] = useState(false);
  const [requireLevel, setRequireLevel] = useState(false);
  const [minLevel, setMinLevel] = useState(1);
  const [requireQuest, setRequireQuest] = useState(false);
  const [selectedQuest, setSelectedQuest] = useState('');
  const [requireItem, setRequireItem] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');

  useEffect(() => {
    if (!editorSystem) return;

    const loadTeleporters = () => {
      const allTeleporters = editorSystem.getEntitiesByType('teleporter');
      const teleporterEntities: TeleporterData[] = allTeleporters.map((e: LevelEntity) => ({
        entity: e,
        link: e.properties?.teleporterLink as TeleporterLink,
        collidesWithProjectiles: e.properties?.teleporterConfig?.collidesWithProjectiles,
        isSelected: e.id === selectedTeleporterId
      }));
      setTeleporters(teleporterEntities);
    };

    loadTeleporters();
    const interval = setInterval(loadTeleporters, 1000);
    return () => clearInterval(interval);
  }, [editorSystem, selectedTeleporterId]);

  const prevSelectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedTeleporterId !== prevSelectedIdRef.current) {
      prevSelectedIdRef.current = selectedTeleporterId;

      const selected = teleporters.find(t => t.entity.id === selectedTeleporterId);
      if (selected?.link) {
        setTargetId(selected.link.targetId || '');
        setTargetMapId(selected.link.targetMapId || '');
        setRequireLevel(!!selected.link.conditions?.minLevel);
        setMinLevel(selected.link.conditions?.minLevel || 1);
        setRequireQuest(!!selected.link.conditions?.requiredQuest);
        setSelectedQuest(selected.link.conditions?.requiredQuest || '');
        setRequireItem(!!selected.link.conditions?.requiredItem);
        setSelectedItem(selected.link.conditions?.requiredItem || '');
      } else {
        setTargetId('');
        setTargetMapId('');
        setRequireLevel(false);
        setMinLevel(1);
        setRequireQuest(false);
        setSelectedQuest('');
        setRequireItem(false);
        setSelectedItem('');
      }

      setCollidesWithProjectiles(!!selected?.collidesWithProjectiles);
      setIsLinkingMode(false);
    }
  }, [selectedTeleporterId, teleporters]);

  const handleSaveLinkWithTarget = useCallback((newTargetId: string) => {
    if (!selectedTeleporterId || !editorSystem) return;

    // Reject a self-referential link (a portal to itself). The click-to-link
    // path already guards this; guard the manual save path too.
    if (newTargetId && newTargetId === selectedTeleporterId) {
      gameLogger.warn('editor', 'Refused self-referential teleporter link', { id: selectedTeleporterId });
      return;
    }

    const selectedTeleporter = teleporters.find(t => t.entity.id === selectedTeleporterId);
    const link: TeleporterLink = {
      targetId: newTargetId || undefined,
      targetMapId: targetMapId || undefined,
      conditions: {}
    };

    if (requireLevel && minLevel > 1) link.conditions!.minLevel = minLevel;
    if (requireQuest && selectedQuest) link.conditions!.requiredQuest = selectedQuest;
    if (requireItem && selectedItem) link.conditions!.requiredItem = selectedItem;

    const success = editorSystem.updateEntityProperty(selectedTeleporterId, 'teleporterLink', link);
    editorSystem.updateEntityProperty(selectedTeleporterId, 'teleporterConfig', {
      ...(selectedTeleporter?.entity?.properties?.teleporterConfig || {}),
      collidesWithProjectiles
    });

    if (success) {
      gameLogger.info('editor', 'Teleporter link saved automatically', { sourceId: selectedTeleporterId, link });
    }
  }, [selectedTeleporterId, targetMapId, requireLevel, minLevel, requireQuest, selectedQuest, requireItem, selectedItem, collidesWithProjectiles, teleporters, editorSystem]);

  useEffect(() => {
    if (!isLinkingMode) return;

    const handleEntitySelected = (data: { entity: any }) => {
      const { entity } = data;
      if (entity && entity.type === 'teleporter' && entity.id !== selectedTeleporterId) {
        setTargetId(entity.id);
        setIsLinkingMode(false);
        if (editorSystem) editorSystem.setTool(EditorTool.SELECT);
        handleSaveLinkWithTarget(entity.id);
      }
    };

    const unsubscribe = EventBus.getInstance().on('editor:entitySelected', handleEntitySelected);
    return () => unsubscribe();
  }, [isLinkingMode, selectedTeleporterId, editorSystem, targetMapId, minLevel, requireLevel, requireQuest, requireItem, selectedQuest, selectedItem, collidesWithProjectiles, teleporters, handleSaveLinkWithTarget]);

  const handleSaveLink = useCallback(() => {
    handleSaveLinkWithTarget(targetId);
  }, [handleSaveLinkWithTarget, targetId]);

  const handleTestTeleport = useCallback(() => {
    if (!selectedTeleporterId) return;
    gameLogger.info('editor', 'Testing teleport from', selectedTeleporterId);
  }, [selectedTeleporterId]);

  const handleDeleteTeleporter = useCallback(() => {
    if (!selectedTeleporterId || !editorSystem) return;
    if (window.confirm('Delete this teleporter?')) {
      if (editorSystem.deleteEntity(selectedTeleporterId)) setSelectedTeleporterId(null);
    }
  }, [selectedTeleporterId, editorSystem]);

  const linkedTeleporters = useMemo(() => teleporters.filter(t => t.link?.targetId), [teleporters]);
  const unlinkedTeleporters = useMemo(() => teleporters.filter(t => !t.link?.targetId), [teleporters]);

  return {
    state: {
      teleporters, selectedTeleporterId, showLinkLines, linkColor, isLinkingMode,
      targetId, targetMapId, collidesWithProjectiles, requireLevel, minLevel,
      requireQuest, selectedQuest, requireItem, selectedItem,
      linkedTeleporters, unlinkedTeleporters
    },
    actions: {
      setSelectedTeleporterId, setShowLinkLines, setLinkColor, setIsLinkingMode,
      setTargetId, setTargetMapId, setCollidesWithProjectiles, setRequireLevel, setMinLevel,
      setRequireQuest, setSelectedQuest, setRequireItem, setSelectedItem,
      handleSaveLink, handleSaveLinkWithTarget, handleTestTeleport, handleDeleteTeleporter
    }
  };
}
